/**
 * @file Scrolling loader component.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import classNames from 'classnames';

import {
  getPercentWindowScrolled,
  hasVerticalScroll,
  getPercentTargetScrolled,
} from '../../util/scroll';
import Spinner from './Spinner';

class ScrollLoader extends Component {
  static propTypes = {
    loadMore: PropTypes.func.isRequired,
    shouldLoadMore: PropTypes.bool.isRequired,
    scrollPercentage: PropTypes.number,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    loadOnWheel: PropTypes.bool,
    onMoreLoaded: PropTypes.func,
    target: PropTypes.object,
    spinner: PropTypes.bool,
    spinnerThreshold: PropTypes.number,
    className: PropTypes.string,
  };

  static defaultProps = {
    children: null,
    scrollPercentage: 75,
    loadOnWheel: false,
    spinnerThreshold: 90,
  };

  state = {
    loading: false,
    spinnerRequired: false,
  };

  componentDidMount() {
    const { loadOnWheel, target } = this.props;

    const element = target || window;

    element.addEventListener('scroll', this.handleScrollDebounced);
    if (loadOnWheel) {
      element.addEventListener('wheel', this.handleWheelDebounced);
    }
  }

  componentDidUpdate(prevProps) {
    const { loadOnWheel, target } = this.props;

    if (!prevProps.target && !!target) {
      target.addEventListener('scroll', this.handleScrollDebounced);
      window.removeEventListener('scroll', this.handleScrollDebounced);
      if (loadOnWheel) {
        target.addEventListener('wheel', this.handleWheelDebounced);
        window.removeEventListener('wheel', this.handleWheelDebounced);
      }
    }
  }

  componentWillUnmount() {
    const { loadOnWheel, target } = this.props;

    const element = target || window;

    element.removeEventListener('scroll', this.handleScrollDebounced);
    if (loadOnWheel) {
      element.removeEventListener('wheel', this.handleWheelDebounced);
    }
  }

  /**
   * Simple heuristic on whether to show the spinner or not.
   * It really should only be shown if the user has scrolled far enough
   * to actually require a visual indication that they should
   * expect something to be loading.
   */
  shouldShowSpinner = () => {
    const { target, spinnerThreshold } = this.props;
    const currentScrollPercentage = target
      ? getPercentTargetScrolled(target)
      : getPercentWindowScrolled();

    return currentScrollPercentage > spinnerThreshold;
  };

  /**
   * Listens to the mousewheel scroll to load data
   * when there's no scrollbars. Though this is a rare
   * case, it might happen when deleting pictures.
   *
   * @param {Event} event The browser wheel event.
   */
  handleWheel = async event => {
    const { loading } = this.state;
    const { loadMore, shouldLoadMore, onMoreLoaded, spinner } = this.props;

    if (event.deltaY < 0) {
      // Do nothing on scrollTop.
      return;
    }

    if (shouldLoadMore && !loading && !hasVerticalScroll()) {
      this.setState(() => ({
        loading: true,
        spinnerRequired: this.shouldShowSpinner(),
      }));
      await loadMore();
      this.setState(() => ({
        loading: false,
        spinnerRequired: false,
      }));

      if (onMoreLoaded) {
        onMoreLoaded();
      }
    }

    if (spinner) {
      this.setState(() => ({
        spinnerRequired: loading && this.shouldShowSpinner(),
      }));
    }
  };

  handleScroll = async () => {
    const { loading } = this.state;
    const {
      loadMore,
      shouldLoadMore,
      scrollPercentage,
      target,
      onMoreLoaded,
      spinner,
    } = this.props;

    const currentScrollPercentage = target
      ? getPercentTargetScrolled(target)
      : getPercentWindowScrolled();

    if (
      shouldLoadMore &&
      !loading &&
      currentScrollPercentage > scrollPercentage
    ) {
      this.setState(() => ({
        loading: true,
        spinnerRequired: this.shouldShowSpinner(),
      }));
      await loadMore();
      this.setState(() => ({
        loading: false,
        spinnerRequired: false,
      }));

      if (onMoreLoaded) {
        onMoreLoaded();
      }
    }

    if (spinner) {
      this.setState(() => ({
        spinnerRequired: loading && this.shouldShowSpinner(),
      }));
    }
  };

  // eslint-disable-next-line react/sort-comp
  handleScrollDebounced = debounce(this.handleScroll, 100, {
    leading: true,
  });

  handleWheelDebounced = debounce(this.handleWheel, 100, {
    leading: true,
  });

  render() {
    const { loading, spinnerRequired } = this.state;
    const { spinner, className } = this.props;
    const spinnerClasses = classNames({
      'scroll-load-spinner': true,
      'scroll-load-spinner-visible': loading,
      [className]: !!className,
    });

    return (
      <div>
        {spinner && spinnerRequired && (
          <span className={spinnerClasses}>
            <Spinner />
          </span>
        )}
        {this.props.children}
      </div>
    );
  }
}

export default ScrollLoader;
